import React, { useContext, useEffect, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlus, faTimes } from '@fortawesome/free-solid-svg-icons';
import { useLimitList } from 'js/hooks';
import { api, AbortError, RuleSet, getSeasonalMessage } from 'js/utils';
import Alert from 'js/components/alert/alert';
import Box from 'js/components/box/box';
import Button from 'js/components/button/button';
import Container from 'js/components/container/container';
import Drawer from 'js/components/drawer/drawer';
import DrawerFooter from 'js/components/drawer/footer';
import Form from 'js/components/form/form';
import Col from 'js/components/grid/column';
import Row from 'js/components/grid/row';
import SectionTitle from 'js/components/section-title/section-title';
import TextArea from 'js/components/textarea/textarea';
import TopicButton from 'js/components/button/topic-button';
import Label from 'js/components/label/label';
import Layout from 'js/components/layout/layout';
import Pill from 'js/components/pill/pill';
import ruleTypes from 'js/enums/rule-types.enum';
import { AI_WAITING_MESSAGES, SEASONAL_MESSAGES } from 'js/constants';
import RandomLoadingMessage from './random-loading-message';
import ContextDetailContext from '../../contexts/context-detail.context';
import styles from './ai-context-builder-drawer.module.scss';

const URL_REGEX = /(https?:\/\/\S+|www\.\S+)/;

const ValidationError = () => (
    <Alert theme="danger">
        <p>
            You cannot submit your query at the moment because there are
            problems that have been highlighted in the form above.
        </p>
    </Alert>
);

const EmptyResult = ({ isVideo }) => (
    <Alert theme="empty" title="No Results Found">
        <p>
            We didn’t come up with anything. Try changing your prompt to
            generate new topics{!isVideo && ' and keywords'}.
        </p>
    </Alert>
);

const QueryError = ({ onRetry }) => (
    <Alert
        theme="danger"
        title="There was an unexpected error when querying the AI"
        actionItems={<Button onClick={onRetry}>Retry</Button>}
    >
        <p>
            Hopefully it is only a temporary issue. Please try again in a few
            moments.
        </p>
    </Alert>
);

const KeywordPill = ({ active, children, onClick }) => (
    <Pill
        outline={!active}
        theme={active ? 'keyword' : 'muted'}
        suffixButton={
            onClick ? (
                <FontAwesomeIcon icon={active ? faTimes : faPlus} />
            ) : null
        }
        onSuffixClick={onClick}
    >
        {children}
    </Pill>
);

const GetStarted = () => (
    <Alert
        theme="empty"
        title="Build Out Your Context with the Power of Generative AI"
    >
        <p>
            Expedite the creation of your context with the power of AI. Simply
            provide a description of the context you wish to target; we’ll use
            large language models to generate relevant keywords and topics.
        </p>
    </Alert>
);

const AIContextBuilderPrompt = ({
    value,
    loading,
    errors,
    onChange,
    onSubmit,
}) => (
    <Form onSubmit={onSubmit}>
        <Label
            errorMessage={<p>{errors.user_data}</p>}
            hasError={!!errors.user_data}
        >
            <TextArea
                focusOnShow
                disabled={loading}
                rows={2}
                placeholder={getSeasonalMessage(SEASONAL_MESSAGES)}
                onChange={(ev) => onChange(ev.target.value)}
                value={value}
            />
        </Label>
        <Row justifyContent="flex-end" gutter="none">
            <Box display="inline-block" padding={['small', 0, 0]}>
                <Button type="submit" disabled={loading || !value.trim()}>
                    Generate
                </Button>
            </Box>
        </Row>
    </Form>
);

const AIContextBuilderContent = ({
    loading,
    errors,
    keywords,
    topics,
    isVideo,
    rules,
    onChangeRules,
    onRetry,
}) => {
    const firstRule = rules.find(
        ({ aggregation }) => aggregation === ruleTypes.INCLUDED,
    );

    const targetedKeywords = rules
        .filter((item) => item.aggregation === ruleTypes.INCLUDED)
        .map((item) => item.keywords)
        .flat();
    const blockedKeywords = rules
        .filter((item) => item.aggregation === ruleTypes.EXCLUDED)
        .map((item) => item.keywords)
        .flat();
    const availableKeywords = keywords.filter(
        (keyword) => !blockedKeywords.includes(keyword),
    );

    const targetedTopicIds = rules
        .filter((item) => item.aggregation === ruleTypes.INCLUDED)
        .map((item) => item.topics)
        .flat();
    const blockedTopicIds = rules
        .filter((item) => item.aggregation === ruleTypes.EXCLUDED)
        .map((item) => item.topics)
        .flat();
    const availableTopics = topics.filter(
        (topic) =>
            !blockedTopicIds.includes(topic.id) && !topic.is_brand_safety,
    );

    const [
        hasMoreKeywords,
        isKeywordsExpanded,
        visibleKeywords,
        showMoreKeywords,
        showLessKeywords,
    ] = useLimitList(availableKeywords, 30);

    const [
        hasMoreTopics,
        isTopicsExpanded,
        visibleTopics,
        showMoreTopics,
        showLessTopics,
    ] = useLimitList(availableTopics, 30);

    const addKeywords = (newKeywords) => {
        const ruleSet = new RuleSet(rules);
        ruleSet.changeRule(firstRule.key, { addKeywords: newKeywords });
        onChangeRules(ruleSet.rules);
    };
    const removeKeyword = (keyword) => {
        const ruleSet = new RuleSet(rules);
        ruleSet.changeAllRules({ removeKeywords: [keyword] });
        onChangeRules(ruleSet.rules);
    };

    const addTopics = (newTopics) => {
        const ruleSet = new RuleSet(rules);
        ruleSet.changeRule(firstRule.key, {
            addTopics: newTopics.map(({ id }) => id),
        });
        onChangeRules(ruleSet.rules);
    };
    const removeTopic = (topic) => {
        const ruleSet = new RuleSet(rules);
        ruleSet.changeAllRules({ removeTopics: [topic.id] });
        onChangeRules(ruleSet.rules);
    };

    if (loading) {
        return <RandomLoadingMessage messages={AI_WAITING_MESSAGES} />;
    }
    if (Object.keys(errors).length) {
        return errors.user_data ? (
            <ValidationError />
        ) : (
            <QueryError onRetry={onRetry} />
        );
    }
    if (!topics.length && !keywords.length) {
        return <GetStarted />;
    }
    if (!availableTopics.length && !availableKeywords.length) {
        return <EmptyResult isVideo={isVideo} />;
    }

    return (
        <>
            <Box margin={['large', 0]}>
                {availableTopics.length > 0 && (
                    <SectionTitle
                        hasBar
                        extension={[
                            <Button
                                theme="outline"
                                size="small"
                                disabled={availableTopics.every((topic) =>
                                    targetedTopicIds.includes(topic.id),
                                )}
                                onClick={() => addTopics(availableTopics)}
                            >
                                Add All Topics
                            </Button>,
                        ]}
                    >
                        Topics
                    </SectionTitle>
                )}
                {visibleTopics.map((topic) => (
                    <Box
                        key={topic.id}
                        margin={['small', 'small', 0, 0]}
                        display="inline-block"
                    >
                        <TopicButton
                            logo={topic.group_logo}
                            action={
                                targetedTopicIds.includes(topic.id)
                                    ? 'remove'
                                    : 'add'
                            }
                            theme={
                                targetedTopicIds.includes(topic.id)
                                    ? 'targeted'
                                    : 'default'
                            }
                            onClick={
                                targetedTopicIds.includes(topic.id)
                                    ? () => removeTopic(topic)
                                    : () => addTopics([topic])
                            }
                        >
                            {topic.name}
                        </TopicButton>
                    </Box>
                ))}

                {hasMoreTopics && (
                    <Box margin={['small', 'small', 0, 0]}>
                        <Row>
                            <Col span="auto">
                                <Button
                                    theme="outline"
                                    size="small"
                                    onClick={showMoreTopics}
                                >
                                    Show All {topics.length} Topics
                                </Button>
                            </Col>
                        </Row>
                    </Box>
                )}

                {isTopicsExpanded && (
                    <Box margin={['small', 'small', 0, 0]}>
                        <Row>
                            <Col span="auto">
                                <Button
                                    theme="outline"
                                    size="small"
                                    onClick={showLessTopics}
                                >
                                    Show Fewer Topics
                                </Button>
                            </Col>
                        </Row>
                    </Box>
                )}
            </Box>

            <Box margin={['large', 0]}>
                {availableKeywords.length > 0 && (
                    <SectionTitle
                        hasBar
                        extension={[
                            <Button
                                theme="outline"
                                size="small"
                                disabled={availableKeywords.every((keyword) =>
                                    targetedKeywords.includes(keyword),
                                )}
                                onClick={() => addKeywords(availableKeywords)}
                            >
                                Add All Keywords
                            </Button>,
                        ]}
                    >
                        Keywords
                    </SectionTitle>
                )}
                {visibleKeywords.map((keyword) => (
                    <Box
                        key={keyword}
                        margin={['small', 'small', 0, 0]}
                        display="inline-block"
                    >
                        <KeywordPill
                            active={targetedKeywords.includes(keyword)}
                            onClick={
                                targetedKeywords.includes(keyword)
                                    ? () => removeKeyword(keyword)
                                    : () => addKeywords([keyword])
                            }
                        >
                            {keyword}
                        </KeywordPill>
                    </Box>
                ))}

                {hasMoreKeywords && (
                    <Box margin={['small', 'small', 0, 0]}>
                        <Row>
                            <Col span="auto">
                                <Button
                                    theme="outline"
                                    size="small"
                                    onClick={showMoreKeywords}
                                >
                                    Show All {keywords.length} Keywords
                                </Button>
                            </Col>
                        </Row>
                    </Box>
                )}

                {isKeywordsExpanded && (
                    <Box margin={['small', 'small', 0, 0]}>
                        <Row>
                            <Col span="auto">
                                <Button
                                    theme="outline"
                                    size="small"
                                    onClick={showLessKeywords}
                                >
                                    Show Fewer Keywords
                                </Button>
                            </Col>
                        </Row>
                    </Box>
                )}
            </Box>
        </>
    );
};

export default function AIContextBuilderDrawer({ onClose }) {
    const [prompt, setPrompt] = useState('');
    const [keywords, setKeywords] = useState([]);
    const [topics, setTopics] = useState([]);
    const [loading, setLoading] = useState(false);
    const [errors, setErrors] = useState({});
    const {
        context: {
            current: {
                is_video: isVideo,
                is_app: isApp,
                language,
                rules,
                strategy,
            },
            setRules,
        },
        topics: { list: allTopics },
    } = useContext(ContextDetailContext);

    useEffect(() => () => api().abortAll(), []);

    const executeQuery = async () => {
        setKeywords([]);
        setTopics([]);
        setErrors({});
        setLoading(true);
        try {
            if (URL_REGEX.test(prompt)) {
                const error = {
                    user_data:
                        'AI Context Builder does not support URLs yet. ' +
                        'Please enter a prompt describing what you’d like to target.',
                };
                throw error;
            }

            const response = await api().aiContextBuilder.create({
                user_data: prompt,
                language: isVideo ? 'en' : language,
                matching_strategy: strategy,
                is_video: isVideo,
                is_app: isApp,
            });

            setKeywords(response.keywords);
            setTopics(
                response.topics.map((topicId) =>
                    allTopics.find(({ id }) => id === topicId),
                ),
            );
        } catch (err) {
            if (err instanceof AbortError) return;
            setErrors(err);
        }
        setLoading(false);
    };

    return (
        <Drawer onClose={onClose}>
            <Layout
                header={
                    <div className={styles.header}>
                        <Container size="medium">
                            <Box padding={['base', 0]}>
                                <AIContextBuilderPrompt
                                    value={prompt}
                                    loading={loading}
                                    errors={errors}
                                    onChange={(newPrompt) => {
                                        setPrompt(newPrompt);
                                        setErrors({});
                                    }}
                                    onSubmit={executeQuery}
                                />
                            </Box>
                        </Container>
                    </div>
                }
                footer={
                    <DrawerFooter>
                        <Container size="medium">
                            <Box padding={['small', 0]}>
                                <Row justifyContent="flex-end">
                                    <Col span="auto">
                                        <Button onClick={onClose}>Done</Button>
                                    </Col>
                                </Row>
                            </Box>
                        </Container>
                    </DrawerFooter>
                }
                isFooterSticky
            >
                <Container size="medium">
                    <Box margin={['large', 0]}>
                        <AIContextBuilderContent
                            loading={loading}
                            errors={errors}
                            keywords={keywords}
                            topics={topics}
                            isVideo={isVideo}
                            rules={rules}
                            onChangeRules={setRules}
                            onRetry={executeQuery}
                        />
                    </Box>
                </Container>
            </Layout>
        </Drawer>
    );
}
